/**
 * Session expiry — two rules, one clock.
 *
 * 1. IDLE (QA 86cb9yd3p item 1). Ten minutes without user input ends the
 *    session. This also kills the stale-state problem — a player who walks away
 *    for hours can never come back to a long-dead screen full of old numbers;
 *    they re-login and every screen refetches fresh on mount.
 *
 * 2. AWAY (QA 86cb9yd3p item 2 — "closed tab, 10 mins auto log out"). The idle
 *    timer dies with the page, and the refresh cookie outlives it by days, so
 *    closing the tab and coming back tomorrow silently restored the session:
 *    rule 1 alone was only ever enforced while somebody was watching. So the
 *    last moment the player was present is stamped where it survives the tab
 *    closing, and a session restored from the cookie is discarded if that stamp
 *    is older than the same ten minutes.
 *
 * A hidden tab's timers are throttled by the browser, so the interval alone
 * can't be trusted — the visibilitychange handler re-checks the moment the
 * player returns, before they can interact with anything stale.
 */
export const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

/** Where "the player was last here" is kept. localStorage, not sessionStorage:
 *  sessionStorage dies with the tab, which is precisely the case rule 2 exists
 *  to catch. */
const LAST_SEEN_KEY = 'caligo:last-seen';

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
const CHECK_EVERY_MS = 30_000;
/** Floor on writes — pointermove fires per frame and localStorage is sync. */
const STAMP_EVERY_MS = 5_000;

/** Stamp "the player is here". Safe when storage is unavailable (private mode). */
export function markSeen(at: number = Date.now()): void {
  try { localStorage.setItem(LAST_SEEN_KEY, String(at)); } catch { /* no storage, rule 2 just won't fire */ }
}

/** Drop the stamp — on an explicit logout, so the next boot reads nothing. */
export function clearSeen(): void {
  try { localStorage.removeItem(LAST_SEEN_KEY); } catch { /* nothing to clear */ }
}

/** Ms since the player was last present, or null if there is no usable stamp. */
export function msSinceSeen(now: number = Date.now()): number | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(LAST_SEEN_KEY); } catch { return null; }
  if (raw === null) return null;
  const at = Number(raw);
  if (!Number.isFinite(at) || at <= 0) return null;
  // A stamp from the future means a clock change, not presence — treat it as now.
  return Math.max(0, now - at);
}

/**
 * Should a session restored from the refresh cookie be thrown away?
 *
 * Only ever true when there IS a stamp and it is stale. No stamp is NOT
 * evidence of an abandoned session — it is a first visit, a cleared browser or
 * private mode — and kicking those to the login screen would break a legitimate
 * fresh sign-in.
 */
export function sessionExpiredWhileAway(now: number = Date.now(), timeoutMs: number = IDLE_TIMEOUT_MS): boolean {
  const since = msSinceSeen(now);
  return since !== null && since >= timeoutMs;
}

/** Start watching; returns a teardown. `onExpire` fires at most once. */
export function installIdleLogout(onExpire: () => void, timeoutMs = IDLE_TIMEOUT_MS): () => void {
  let lastActivity = Date.now();
  let lastStamp = 0;
  let expired = false;

  const stamp = (now: number) => {
    if (now - lastStamp < STAMP_EVERY_MS) return;
    lastStamp = now;
    markSeen(now);
  };

  const bump = () => {
    const now = Date.now();
    lastActivity = now;
    stamp(now);
  };
  const check = () => {
    if (expired || Date.now() - lastActivity < timeoutMs) return;
    expired = true;
    teardown();
    onExpire();
  };
  const onVisible = () => {
    if (document.hidden) {
      // Leaving: record the moment, so a tab closed from here is timed from now.
      markSeen();
      return;
    }
    check();
  };
  // Last write before the page goes away — pagehide fires on close and on
  // bfcache eviction, where unload is unreliable.
  const onHide = () => markSeen();

  // The session starts now; stamp immediately so a tab closed seconds later
  // still has something to measure from.
  markSeen(lastActivity);
  lastStamp = lastActivity;

  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pagehide', onHide);
  const timer = window.setInterval(check, CHECK_EVERY_MS);

  function teardown() {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pagehide', onHide);
    window.clearInterval(timer);
  }
  return teardown;
}
